import React, { useMemo } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '@frontend/shadcn/components/ui/carousel';
import xss from 'xss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Button } from '@frontend/shadcn/components/ui/button';
import { iconOptions } from '@frontend/app/containers/Projects/CampaignPage/components/ImageIconSelector';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertTitle } from '@frontend/shadcn/components/ui/alert';
import { CheckIcon, HashtagIcon, CameraIcon } from '@revfluence/fresh-icons/solid/esm';
import { InstagramIcon, TiktokIcon, YoutubeIcon, PinterestIcon } from '@revfluence/fresh-icons/brands/esm';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { ViewMode } from '../../constants';
import { formatCampaignDateRange } from '../../utils/formatCampaignDateRange';
import { getSocialIcon } from '../../utils/getSocialIcon';
import { isVideoUrl } from '@/app/refresh-components/utils/mediaUtils';
import { DEFAULT_AVAILABILITY_ITEMS, ListItem } from '../../RecruitmentContext';

// Define Zod schema for form validation
const heroFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type HeroFormData = z.infer<typeof heroFormSchema>;

interface HeroProps {
  view: ViewMode;
  logo: string;
  title: string;
  description: string;
  joinButtonText: string;
  campaignStartDate?: Date;
  campaignEndDate?: Date;
  images: { url: string; alt: string }[];
  socialMedia: {
    platform: string;
    url: string;
  }[];
  listItems: {
    text: string;
    icon: string;
  }[];
  listStyleType: 'number' | 'bullet' | 'icon' | 'none';
  backgroundColor: string;
  bodyTextColor: string;
  bodyTextFontSize: number;
  bodyTextFontFamily: string;
  titleColor: string;
  titleFontSize: number;
  titleFontFamily: string;
  joinButtonColor: string;
  joinButtonTextColor: string;
  joinButtonFontSize: number;
  joinButtonFontFamily: string;
  backgroundColorSecondary: string;
  bodyTextColorSecondary: string;
  footerBrand?: string;
  footerLinks?: { label: string; href: string }[];
  onSubmit?: (data: HeroFormData) => void;
  isPublished: boolean;
  compensationTitle: string;
  compensation: ListItem[];
  showCompensation: boolean;
  availabilityTitle: string;
  availability: ListItem[];
  showAvailability: boolean;
  listItemsTitle: string;
  showListItems: boolean;
}

export const Hero = ({
  view,
  logo,
  title,
  description,
  joinButtonText,
  campaignStartDate,
  campaignEndDate,
  images,
  backgroundColor,
  bodyTextColor,
  bodyTextFontSize,
  bodyTextFontFamily,
  titleColor,
  titleFontSize,
  titleFontFamily,
  joinButtonColor,
  joinButtonTextColor,
  joinButtonFontSize,
  joinButtonFontFamily,
  socialMedia,
  backgroundColorSecondary,
  bodyTextColorSecondary,
  footerBrand = 'Aspire',
  footerLinks = [
    { label: 'Terms of Service', href: '#' },
    { label: 'Privacy Policy', href: '#' },
  ],
  listItems,
  listStyleType,
  onSubmit,
  isPublished,
  compensationTitle,
  compensation,
  showCompensation,
  availabilityTitle,
  availability,
  showAvailability,
  listItemsTitle,
  showListItems,
}: HeroProps) => {
  const isDesktop = view === ViewMode.DESKTOP;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<HeroFormData>({
    resolver: zodResolver(heroFormSchema),
  });

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit?.(data);
  });

  function getAvailabilityIcon(itemText: string) {
    const itemFromDefault = DEFAULT_AVAILABILITY_ITEMS.find((item) => item === itemText);
    if (itemFromDefault === 'Instagram') return <InstagramIcon className="w-4 h-4" />;
    if (itemFromDefault === 'TikTok') return <TiktokIcon className="w-4 h-4" />;
    if (itemFromDefault === 'YouTube') return <YoutubeIcon className="w-4 h-4" />;
    if (itemFromDefault === 'Pinterest') return <PinterestIcon className="w-4 h-4" />;
    if (itemFromDefault === 'User Generated Content (UGC)') return <CameraIcon className="w-4 h-4" />;
    return <HashtagIcon className="w-4 h-4" />;
  }

  function renderPrefix(
    idx: number,
    listItem: {
      text: string;
      icon: string;
    },
  ) {
    switch (listStyleType) {
      case 'number':
        return (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-white font-semibold">
            {idx + 1}
          </span>
        );
      case 'bullet':
        return <span className="text-base font-bold mr-2">•</span>;
      case 'icon': {
        const iconObj = iconOptions.find((icon) => icon.name === listItem.icon);
        const iconToUse = iconObj ? iconObj.icon : faBriefcase;
        return <FontAwesomeIcon icon={iconToUse} className="w-4 h-4 align-middle" />;
      }
      case 'none':
      default:
        return null;
    }
  }
  const titleStyle = {
    color: titleColor,
    fontSize: titleFontSize,
    fontFamily: titleFontFamily,
    fontWeight: 600,
  };

  const bodyStyle = {
    color: bodyTextColor,
    fontSize: bodyTextFontSize,
    fontFamily: bodyTextFontFamily,
  };

  const buttonStyle = {
    background: joinButtonColor,
    color: joinButtonTextColor,
    fontSize: joinButtonFontSize,
    fontFamily: joinButtonFontFamily,
  };

  const date = useMemo(
    () => formatCampaignDateRange(campaignStartDate, campaignEndDate),
    [campaignStartDate, campaignEndDate],
  );

  if (isDesktop) {
    return (
      <>
        <style>
          {`
      .reset-inner-margin * {
        margin: 0 !important;
      }
    `}
        </style>
        <div className="w-full h-screen relative p-8 flex flex-col" style={{ background: backgroundColor }}>
          {/* Top Section */}
          <div className="w-full flex justify-between items-start mb-8">
            <img className="w-10 h-10 rounded-full bg-white" src={logo} alt="Logo" />
            {date && <div className="px-3 py-1 bg-gray-200 rounded-lg text-gray-800 text-xs font-medium">{date}</div>}
          </div>

          {/* Main Content */}
          <div className="flex-1 flex gap-16">
            {/* Left Content */}
            <div className="flex-1 flex flex-col">
              <div className="mb-4">
                <h1 className="mb-4" style={titleStyle}>
                  {title}
                </h1>
                <div style={bodyStyle} dangerouslySetInnerHTML={{ __html: xss(description) }} />
              </div>

              {showCompensation && (
                <div className="mb-4 text-base">
                  <h3 className="mb-0 !text-base" style={titleStyle}>
                    {compensationTitle}
                  </h3>
                  <div className="flex flex-col">
                    {compensation.filter((item) => item.checked).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3" style={bodyStyle}>
                        <CheckIcon />
                        <span>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showAvailability && (
                <div className="mb-4 text-base">
                  <h3 className="mb-2 !text-base" style={titleStyle}>
                    {availabilityTitle}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availability.filter((item) => item.checked).map((item, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-2 text-sm">
                        {getAvailabilityIcon(item.text)}
                        <span>{item.text}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* White Card */}
              <div
                className="rounded-xl p-6 flex flex-col gap-6"
                style={{ background: backgroundColorSecondary, color: bodyTextColorSecondary }}
              >
                {showListItems && (
                  <>
                    <h2 className="font-medium text-lg">{listItemsTitle}</h2>

                    <div className="flex flex-col gap-4">
                      {listItems?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          {renderPrefix(idx, item)}
                          <span
                            className="text-sm reset-inner-margin"
                            dangerouslySetInnerHTML={{ __html: xss(item.text) }}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className="flex flex-col gap-4">
                  <form onSubmit={handleFormSubmit}>
                    <Input placeholder="Email Address" className="bg-white border-gray-200" {...register('email')} />
                    {errors.email && <span className="text-xs text-red-500">{errors.email.message}</span>}

                    <div className="flex flex-col gap-2 mt-2">
                      {!isPublished && (
                        <Alert variant="default" className="w-full">
                          <AlertTitle>We are no longer accepting applications for this program.</AlertTitle>
                        </Alert>
                      )}
                      <Button type="submit" className="w-full" style={buttonStyle} disabled={!isPublished}>
                        {joinButtonText}
                      </Button>
                    </div>
                  </form>
                </div>
                {/* Social Media Section - moved here */}
                {socialMedia.length > 0 && (
                  <div className="w-full flex justify-center items-center gap-4 my-[-8px]">
                    <span className="text-sm" style={{ color: bodyTextColorSecondary }}>
                      Find us on
                    </span>
                    <div className="flex gap-4">
                      {socialMedia.map((social) => (
                        <a
                          key={social.platform}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cursor-pointer"
                          style={{ color: bodyTextColorSecondary }}
                        >
                          {getSocialIcon(social.platform)}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-center gap-4 text-sm">
                  <span className="font-medium">{footerBrand}</span>
                  <div className="flex gap-4">
                    {footerLinks.map((link) => (
                      <a
                        key={link.label}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-900 text-sm"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Carousel */}
            <div className="flex-1">
              <Carousel
                opts={{
                  align: 'center',
                  loop: true,
                }}
                className="w-full"
              >
                <CarouselContent>
                  {images.map((image, index) => (
                    <CarouselItem key={index}>
                      {isVideoUrl(image.url) ? (
                        <video
                          className="w-full rounded-2xl object-cover"
                          style={{ height: '500px' }}
                          src={image.url}
                          controls
                          autoPlay
                          muted
                          loop
                          playsInline
                        />
                      ) : (
                        <img
                          className="w-full rounded-2xl object-cover"
                          style={{ height: '500px' }}
                          src={image.url}
                          alt={image.alt}
                        />
                      )}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Mobile view
  return (
    <>
      <style>
        {`
      .reset-inner-margin * {
        margin: 0 !important;
      }
    `}
      </style>

      <div className="w-full min-h-screen flex flex-col" style={{ background: backgroundColor }}>
        {/* Header Section */}
        <div className="w-full flex flex-col pt-6 px-4">
          <div className="flex w-full justify-between items-center">
            <img className="w-10 h-10 rounded-full bg-white" src={logo} alt="Logo" />
            {date && <div className="px-3 py-1 bg-gray-200 rounded-lg text-gray-800 text-xs font-medium">{date}</div>}
          </div>
        </div>
        {/* Main Content */}
        <div className="flex flex-col gap-4 px-4 pt-8">
          <div className="leading-tight" style={titleStyle}>
            {title}
          </div>
          <div
            className="leading-normal w-full max-w-xs break-words overflow-x-hidden m-0 p-0"
            style={{ ...bodyStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: xss(description) }}
          />
          {showCompensation && (
            <div className="mb-4 text-base">
              <h3 className="mb-0 !text-base" style={titleStyle}>
                {compensationTitle}
              </h3>
              <div className="flex flex-col">
                {compensation.filter((item) => item.checked).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3" style={{ ...bodyStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    <CheckIcon />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showAvailability && (
            <div className="mb-4 text-base">
              <h3 className="mb-2 !text-base" style={titleStyle}>
                {availabilityTitle}
              </h3>
              <div className="flex flex-wrap gap-2">
                {availability.filter((item) => item.checked).map((item, idx) => (
                  <Badge key={idx} variant="secondary" className="flex items-center gap-2 text-sm">
                    {getAvailabilityIcon(item.text)}
                    <span>{item.text}</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Carousel Section */}
        <div className="w-full flex justify-center mt-8 px-2">
          <Carousel
            opts={{
              align: 'center',
              loop: true,
              dragFree: true,
            }}
            className="w-full max-w-sm"
          >
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index} className="px-2">
                  <div className="rounded-2xl overflow-hidden shadow-md">
                    {isVideoUrl(image.url) ? (
                      <video
                        className="w-full aspect-[3/4] object-cover"
                        src={image.url}
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <img className="w-full aspect-[3/4] object-cover" src={image.url} alt={image.alt} />
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Spacer to push form to bottom */}
        <div className="flex-1" />

        {/* Form Section - Now positioned at bottom */}
        <div
          className="w-full p-4 rounded-t-2xl flex flex-col gap-4"
          style={{ background: backgroundColorSecondary, color: bodyTextColorSecondary }}
        >
          {showListItems && (
            <>
              <H3 className="font-medium text-base m-0" style={{ color: bodyTextColorSecondary }}>
                {listItemsTitle}
              </H3>

              <div className="flex flex-col gap-4" style={{ color: bodyTextColorSecondary }}>
                {listItems?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 w-full max-w-xs">
                    {renderPrefix(idx, item)}
                    <span
                      className="text-sm font-normal w-full break-words overflow-x-hidden reset-inner-margin"
                      style={{ color: bodyTextColorSecondary, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: xss(item.text) }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          <form onSubmit={handleFormSubmit}>
            <Input placeholder="Email Address" className="bg-white" {...register('email')} />
            {errors.email && <span className="text-xs text-red-500">{errors.email.message}</span>}
            <div className="flex flex-col gap-2 mt-2">
              {!isPublished && (
                <Alert variant="default" className="w-full">
                  <AlertTitle>We are no longer accepting applications for this program.</AlertTitle>
                </Alert>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={!isPublished}
                style={{ background: joinButtonColor, color: joinButtonTextColor }}
              >
                {joinButtonText}
              </Button>
            </div>
            {/* Social Media Section - moved here */}
            {socialMedia.length > 0 && (
              <div className="w-full flex justify-center items-center gap-4 mt-2" style={{ color: bodyTextColorSecondary }}>
                <span className="text-sm">Find us on</span>
                <div className="flex gap-4">
                  {socialMedia.map((social) => (
                    <a
                      key={social.platform}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-pointer"
                      style={{ color: bodyTextColorSecondary }}
                    >
                      {getSocialIcon(social.platform)}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </form>

          <div className="flex items-center justify-center gap-4 text-sm">
            <span className="font-medium">{footerBrand}</span>
            <div className="flex gap-4">
              {footerLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-900 text-sm"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
